import React, { Component } from 'react';
import styled from 'styled-components';
import {
    SortableContainer,
    SortableElement,
    arrayMove,
} from 'react-sortable-hoc';

import Scrollable from '../../components/shared/Scrollable';
import Button from '../shared/Button';
import { colors, mix } from '../../shared/styles';

const ListContent = styled.div`
    > button {
        position: absolute;
        right: 10px;
        bottom: calc(1em + 2px);
        cursor: pointer;
    }

    .list-content {
        height: calc(100vh - 152px);
        padding-bottom: 70px;
    }

    .simplebar-content {
        overflow-x: hidden;
    }
`;

const StyledTable = styled.table`
    max-width: 100%;
    width: 100%;
    text-align: left;

    &,
    tbody,
    tfoot,
    thead,
    tr,
    th,
    td {
        margin: 0;
        padding: 0;
        border: 0;
        font-size: 100%;
        vertical-align: baseline;
        border-collapse: collapse;
        border-spacing: 0;
        text-align: left;
    }

    & {
        width: 100%;
    }

    thead {
        background: #edf1f2;
        overflow: hidden;
        border-bottom: 1px solid #d9d9db;
    }

    th {
        box-sizing: border-box;
        width: calc((100% - 100px) / 3);
        font-weight: normal;
        text-align: left;
        padding: 15px 20px;

        &:last-of-type {
            width: 95px;
        }
    }

    tbody {
        tr {
            font-weight: lighter;
            background: #f3f3f5;

            &:nth-of-type(odd) {
                background: ${colors.white};
            }
        }
    }

    .actions {
        width: 20%;
        text-align: right;

        .action-btn:last-child {
            margin-right: 0;
        }
    }

    .actions a,
    .actions button {
        position: relative;
        display: inline-block;
        margin: 8px 8px 8px 0;
        padding: 5px 8px;
        color: #fff;
        text-decoration: none;
        background: #e76424;
        border: none !important;
        font-weight: lighter !important;
        font-size: 14px !important;
        line-height: 16px !important;
        cursor: pointer;
        border-radius: 4px;
        border: none;
    }

    .action-btn .tooltip {
        position: absolute;
        font-size: 12px;
        width: 140px;
        color: #ffffff;
        background: #48555b;
        height: 30px;
        line-height: 30px;
        text-align: center;
        visibility: hidden;
        border-radius: 4px;
    }

    .action-btn .tooltip-wide {
        width: 200px;
    }

    .action-btn .tooltip:after {
        content: '';
        position: absolute;
        top: 50%;
        left: 100%;
        margin-top: -8px;
        width: 0;
        height: 0;
        border-left: 8px solid #48555b;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
    }

    .action-btn:hover .tooltip {
        visibility: visible;
        right: 100%;
        top: 50%;
        margin-top: -15px;
        margin-right: 13px;
        z-index: 999;
    }
`;

const StyledSortableRow = styled.tr`
    margin: 0;
    padding: 0;
    border: 0;
    font-size: 100%;
    vertical-align: baseline;
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
    cursor: all-scroll;

    td {
        padding: 10px 20px;

        &:nth-of-type(1) {
            width: 20%;
        }

        &:nth-of-type(2) {
            width: 50%;
        }
    }

    .btn {
        display: block;
        padding: 7px 15px;
        color: #fff;
        text-decoration: none;
        background: #e76424;
        border-radius: 4px;
        border: none !important;
        font-weight: lighter !important;
        font-size: 14px !important;
        cursor: pointer;
    }

    &[style^=position] {
        background: ${mix('e76424', 'ffffff', 5)};
        width: 100%;
        display: block;

        td {
            display: inline-block;
            box-sizing: border-box;
        }

        td:first-of-type {
            line-height: 62px;
            padding-top: 0;
            padding-bottom: 0;
        }

        td:last-of-type {
            display: none;
        }
    }
`;

const SortableRow = SortableElement(({ plasmaScreen, onDelete }) =>
    <StyledSortableRow>
        <td>
            {plasmaScreen.plasmascreenPropertyReference}
        </td>
        <td>
            {`${plasmaScreen.plasmascreenPropertyName}, ${plasmaScreen.plasmascreenPropertyPostCode}`}
        </td>
        <td>
            {plasmaScreen.plasmascreenPropertyStatus}
        </td>
        <td className="actions">
            <button
                className="action-btn"
                onClick={() => {
                    onDelete(plasmaScreen);
                }}
            >
                <i className="fa fa-trash" />
                <span className="tooltip">Delete</span>
            </button>
        </td>
    </StyledSortableRow>
);

const SortableTable = SortableContainer(({ plasmascreens, onDelete }) => {
    return (
        <tbody>
            {Object.keys(plasmascreens).map((plasmascreenId, index) =>
                <SortableRow
                    key={plasmascreenId}
                    index={index}
                    plasmaScreen={plasmascreens[plasmascreenId]}
                    onDelete={onDelete}
                />
            )}
        </tbody>
    );
});

class List extends Component {
    plasmascreens: Object;
    onSortEnd: Function;
    onDelete: Function;
    onClick: Function;

    state = {
        plasmascreens: this.props.plasmascreens,
    };

    constructor(props) {
        super(props);

        this.onSortEnd = this.onSortEnd.bind(this);
        this.onClick = this.onClick.bind(this);
    }

    onSortEnd({ oldIndex, newIndex }) {
        this.setState({
            plasmascreens: arrayMove(
                Object.keys(this.state.plasmascreens).map(plasmascreenId => {
                    return this.state.plasmascreens[plasmascreenId];
                }),
                oldIndex,
                newIndex
            ),
        });
        this.props.onReorder();
    }

    onClick() {
        this.props.onOrderSave(this.state.plasmascreens);
    }

    render() {
        const { onDelete, reordering } = this.props;
        const { plasmascreens } = this.state;

        return (
            <ListContent>
                <Scrollable additionalClassName="list-content">
                    <StyledTable>
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Name</th>
                                <th>Status</th>
                                <th />
                            </tr>
                        </thead>
                        {plasmascreens &&
                            <SortableTable
                                plasmascreens={plasmascreens}
                                onDelete={onDelete}
                                onSortEnd={this.onSortEnd}
                                transitionDuration={150}
                                lockAxis={'y'}
                            />}
                    </StyledTable>
                </Scrollable>
                {plasmascreens &&
                    <Button disabled={!reordering} onClick={this.onClick}>
                        Save Order
                    </Button>}
            </ListContent>
        );
    }
}

export default List;



// WEBPACK FOOTER //
// ./src/components/PlasmaScreens/List.js